import React, { useState, useEffect, useCallback } from "react";
import "../styles/ManageMember.css";
import "../styles/ManageAdvertisingAgent.css";
import SideBar from "../components/SideBar";
import { useNavigate } from "react-router-dom";
import { advertisingAgentAPI } from "../services/advertisingAgentService";
import {
  formatToDDMMYYYY,
  convertHTMLDateToDDMMYYYY,
  convertDDMMYYYYToHTMLDate,
} from "../utils/dateUtils";

function AdvertisingAgentApprovedHistory() {
  const [search, setSearch] = useState("");
  const [type, setType] = useState("all");
  const [approvedDate, setApprovedDate] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [totalApprovedAgents, setTotalApprovedAgents] = useState(0);
  const [filteredAgentsCount, setFilteredAgentsCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const itemsPerPage = 10;
  const navigate = useNavigate();

  // Memoized fetch function
  const fetchApplications = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      // Prepare filters
      const filters = {
        page: currentPage,
        per_page: itemsPerPage,
        status: "approved",
        ...(type !== "all" && { agent_type: type }),
        ...(search && { search: search }),
      };

      // Add date filter if specified
      if (approvedDate !== "all") {
        const dateParts = approvedDate.split('/');
        if (dateParts.length === 3) {
          const apiDate = `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`;
          filters.reviewed_at = apiDate;
        }
      }

      console.log("Fetching with filters:", filters); // Debug log

      const response = await advertisingAgentAPI.getAgentApplications(filters);
      
      console.log("API Response:", response); // Debug log
      
      setApplications(response.applications || []);
      
      // Set pagination info
      if (response.pagination) {
        const total = response.pagination.total || 0;
        setTotalApprovedAgents(total);
        setFilteredAgentsCount(total);
        
        // Calculate total pages
        const pages = Math.ceil(total / itemsPerPage);
        setTotalPages(pages);
        console.log("Total pages calculated:", pages, "from total:", total); // Debug log
      } else {
        // Fallback if no pagination data
        const apps = response.applications || [];
        setApplications(apps);
        setFilteredAgentsCount(apps.length);
        setTotalPages(Math.ceil(apps.length / itemsPerPage));
      }
    } catch (err) {
      setError("Failed to fetch approved applications");
      console.error("Error fetching approved applications:", err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, type, search, approvedDate, itemsPerPage]);

  // Fetch data when dependencies change
  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  // Reset to page 1 when filters change (except currentPage)
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
    // Don't fetch here - let the above useEffect handle it when currentPage changes
  }, [type, search, approvedDate]);

  // Separate effect to handle initial filter changes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentPage === 1) {
        fetchApplications();
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [type, search, approvedDate, currentPage, fetchApplications]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const handleTypeChange = (e) => {
    setType(e.target.value);
  };

  // Handle date filter change
  const handleDateChange = (e) => {
    const htmlDate = e.target.value;
    if (!htmlDate) {
      setApprovedDate("all");
    } else {
      const ddmmyyyyDate = convertHTMLDateToDDMMYYYY(htmlDate);
      setApprovedDate(ddmmyyyyDate);
    }
  };

  // Get current HTML date value for the input
  const getHTMLDateValue = () => {
    return approvedDate === "all"
      ? ""
      : convertDDMMYYYYToHTMLDate(approvedDate);
  };

  // Handle page change
  const handlePageChange = (pageNumber) => {
    console.log("Changing to page:", pageNumber); // Debug log
    setCurrentPage(pageNumber);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      console.log("Going to previous page from", currentPage); // Debug log
      setCurrentPage(prev => prev - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      console.log("Going to next page from", currentPage); // Debug log
      setCurrentPage(prev => prev + 1);
    }
  };

  const goToRequestHistory = () => {
    navigate("/manage-advertising-agent");
  };

  const goToApprovedHistory = () => {
    navigate("/advertising-agent-approved-history");
  };

  const goToRejectedHistory = () => {
    navigate("/advertising-agent-rejected-history");
  };

  const handleRowClick = (application) => {
    navigate("/advertising-agent-details", {
      state: {
        agent: {
          ...application.customer_details,
          application_id: application.application_id,
          agent_type: application.agent_type,
          status: application.status,
          applied_at: application.applied_at,
          reviewed_at: application.reviewed_at,
          rejection_reason: application.rejection_reason,
          photo_url: application.photo_url,
          is_already_agent: application.is_already_agent,
        },
        application: application,
      },
    });
  };

  // Generate page buttons
  const renderPageButtons = () => {
    const buttons = [];
    const maxVisiblePages = 10;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    // Adjust if we're near the start
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    // First page
    if (startPage > 1) {
      buttons.push(
        <button
          key={1}
          className={`page-btn ${currentPage === 1 ? "active" : ""}`}
          onClick={() => handlePageChange(1)}
        >
          1
        </button>
      );
      if (startPage > 2) {
        buttons.push(<span key="ellipsis1" className="page-ellipsis">...</span>);
      }
    }
    
    // Page range
    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          className={`page-btn ${currentPage === i ? "active" : ""}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }
    
    // Last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        buttons.push(<span key="ellipsis2" className="page-ellipsis">...</span>);
      }
      buttons.push(
        <button
          key={totalPages}
          className={`page-btn ${currentPage === totalPages ? "active" : ""}`}
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </button>
      );
    }
    
    return buttons;
  };

  // Calculate current page count
  const currentPageAgentsCount = applications.length;

  return (
    <div className="manage-member-layout">
      <SideBar />
      <div className="main-content">
        <div className="header-bar" style={{border:0, boxShadow:"none"}}>
          <div className="header-top">
            <h2 className="header-title">Manage Customer Advertising Agent</h2>
          </div>

          {/* Approved History Statistics Section */}
          <div className="user-statistics">
            <div className="stat-item">
              <span className="stat-label">Total Approved:</span>
              <span className="stat-value">{totalApprovedAgents}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Filtered:</span>
              <span className="stat-value">{filteredAgentsCount}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Current Page:</span>
              <span className="stat-value">{filteredAgentsCount}</span>
            </div>
          </div>

          <div className="filters">
            <div className="input-group">
              <label>Filter by Type:</label>
              <select
                value={type}
                onChange={handleTypeChange}
                className="filter-select"
              >
                <option value="all">All</option>
                <option value="mini">Mini Advertising Agent</option>
                <option value="senior">Senior Advertising Agent</option>
              </select>
            </div>

            <div className="input-group">
              <label>Filter by Approved Date:</label>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="date"
                  value={getHTMLDateValue()}
                  onChange={handleDateChange}
                  className="date-filter-select"
                />
                {approvedDate !== "all" && (
                  <button 
                    className="clear-date-btn"
                    onClick={() => setApprovedDate("all")}
                    style={{
                      marginLeft: '8px', 
                      padding: '4px 8px',
                      background: '#ff4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
            </div>

            <div className="input-group">
              <label>Search:</label>
              <input
                type="text"
                placeholder="Search by name or email"
                value={search}
                onChange={handleSearchChange}
                className="search-input"
              />
            </div>
          </div>

          <div className="agent-top-bar">
            <div className="top-buttons">
              <button className="tab-btn" onClick={goToRequestHistory}>
                Request History
              </button>
              <button className="tab-btn active" onClick={goToApprovedHistory}>
                Approved History
              </button>
              <button className="tab-btn" onClick={goToRejectedHistory}>
                Rejected History
              </button>
            </div>
          </div>
        </div>

        <div className="table-container">
          <table className="member-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Type</th>
                <th>Request Date</th>
                <th>Approved Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: "center" }}>
                    <div className="loading-message">Loading Applications...</div>
                  </td>
                </tr>
              ) : applications.length > 0 ? (
                applications.map((application, i) => (
                  <tr
                    key={application.application_id || i}
                    onClick={() => handleRowClick(application)}
                    style={{ cursor: "pointer" }}
                  >
                    <td>{application.customer_details?.first_name} {application.customer_details?.last_name}</td>
                    <td>{application.customer_details?.email}</td>
                    <td>
                      {application.agent_type === "senior"
                        ? "Senior Advertising Agent"
                        : "Mini Advertising Agent"}
                    </td>
                    <td>{formatToDDMMYYYY(application.applied_at)}</td>
                    <td>{formatToDDMMYYYY(application.reviewed_at)}</td>
                    <td>
                      <span className={`status approved`}>Approved</span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" style={{ textAlign: "center" }}>
                    {error || (
                      filteredAgentsCount === 0 && totalApprovedAgents > 0 
                        ? "No approved applications match your filters" 
                        : "No approved applications found"
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Pagination */}
          {!loading && totalPages > 0 && (
            <div className="pagination">
              <button
                className="page-btn"
                onClick={handlePrevPage}
                disabled={currentPage === 1}
              >
                &lt;
              </button>
              
              {renderPageButtons()}
              
              <button
                className="page-btn"
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
              >
                &gt;
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdvertisingAgentApprovedHistory;